import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemText, Divider, Button, Grid, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import { QrCodeScanner, Receipt } from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';

const dummyUserPayments = [
    { id: 1, to: 'Coffee Shop', amount: 5.75, date: '2023-10-28' },
    { id: 2, to: 'Bookstore', amount: 22.50, date: '2023-10-27' },
    { id: 3, to: 'Online Subscription', amount: 9.99, date: '2023-10-25' },
];

const UserPaymentPage = () => {
    const { user } = useAuth();
    const [scanResult, setScanResult] = useState('');
    const [showScanner, setShowScanner] = useState(false);

    useEffect(() => {
        if (!showScanner) {
            return;
        }

        const scanner = new Html5QrcodeScanner(
            "reader",
            {
                qrbox: {
                    width: 250,
                    height: 250,
                },
                fps: 5,
            },
            false // verbose
        );

        function onScanSuccess(decodedText, decodedResult) {
            setScanResult(decodedText);
            setShowScanner(false);
            scanner.clear();
        }

        function onScanFailure(error) {
            // console.warn(`Code scan error = ${error}`);
        }

        scanner.render(onScanSuccess, onScanFailure);

        return () => {
            if (scanner) {
                scanner.clear();
            }
        };
    }, [showScanner]);

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Grid container spacing={{ xs: 2, sm: 4 }}>
                <Grid item xs={12} md={5}>
                    <Paper sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        borderRadius: '12px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        gap: 2 
                    }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                            <QrCodeScanner />
                        </Avatar>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 'bold',
                            fontSize: { xs: '1.1rem', sm: '1.3rem' }
                        }}>Scan & Pay</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                            textAlign: 'center', 
                            mb: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>
                            Click the button below to open your camera and scan a QR code to make a payment.
                        </Typography>
                        <Button variant="contained" onClick={() => setShowScanner(s => !s)}>
                            {showScanner ? 'Close Scanner' : 'Open Scanner'}
                        </Button>
                        {showScanner && <div id="reader" style={{ width: '100%', marginTop: '16px' }}></div>}
                        {scanResult && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: '8px', wordBreak: 'break-all' }}>
                                <Typography variant="h6">Last Scan Result:</Typography>
                                <Typography color="text.secondary">{scanResult}</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Receipt color="action" />
                            <Typography variant="h5" sx={{ 
                                fontWeight: 'bold',
                                                            fontSize: { xs: '1.1rem', sm: '1.3rem' }
                        }}>Payment History</Typography>
                        </Box>
                        <List>
                            {dummyUserPayments.map((payment, index) => (
                                <React.Fragment key={payment.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={<Typography fontWeight="bold">{`Paid to: ${payment.to}`}</Typography>}
                                            secondary={payment.date}
                                        />
                                        <Typography variant="h6" fontWeight="bold">{`$${payment.amount.toFixed(2)}`}</Typography>
                                    </ListItem>
                                    {index < dummyUserPayments.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </motion.div>
    );
};

export default UserPaymentPage; 